import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../store';
import {
  appendToModalPriority,
  removeFromModalPriority
} from '../store/ducks/modal-priority/actions';
import { GetFlag } from '../utils/featureFlag';
import { UserService } from '../services/User';
import { loadUserSuccess } from '../store/ducks/user/actions';

export type ModalPriorityKeys =
  | 'NewJourney'
  | 'FabricationNewJourney'
  | 'EgerdauSurvey'
  | 'NewLongSteelOrders'
  | 'CommerceTokenUnavailable'
  | 'LoadingPurchase'
  | 'Instability';

export type ModalPriorityMap = {
  [key in ModalPriorityKeys]: number;
};

export const ModalPriorities: ModalPriorityMap = {
  NewJourney: 8,
  FabricationNewJourney: 7,
  EgerdauSurvey: 10,
  NewLongSteelOrders: 9,
  CommerceTokenUnavailable: 11,
  LoadingPurchase: 12,
  Instability: 13
};

export type ModalPriorityHookTuple = [
  boolean,
  {
    open: () => void;
    close: () => void;
    dismiss: () => void;
    dontShowThisAgain: () => Promise<void>;
  }
];

export const _modalShownKey = (modalId: string) => `modal_shown_${modalId}`;

export default function useModalPriority(
  modalId: string,
  priority: number,
  defaultOpen: boolean,
  feature_flag?: string,
  session_shown_control?: boolean
): ModalPriorityHookTuple {
  const dispatch = useDispatch();
  const alreadyShownControl =
    sessionStorage.getItem(_modalShownKey(modalId)) != null;
  const [isOpen, setIsOpen] = useState(
    defaultOpen && (session_shown_control || true) && !alreadyShownControl
  );
  const state = useSelector((s: ApplicationState) => s.openModalPriority) || [];
  const usr = useSelector((s: ApplicationState) => s.user);
  const modalsToSkip = usr.modalsToSkip || [];
  const skipCurrentModal = modalsToSkip.includes(modalId);
  const flagEnabled = feature_flag ? GetFlag(feature_flag) : true;

  const maxPriority = state.length ? Math.max(...state) : priority;

  useEffect(() => {
    if (defaultOpen && !alreadyShownControl && !skipCurrentModal) {
      setIsOpen(true);
    }
  }, [defaultOpen]);

  useEffect(() => {
    if (isOpen && !skipCurrentModal) {
      dispatch(appendToModalPriority(priority));
    } else {
      dispatch(removeFromModalPriority(priority));
    }
  }, [isOpen, skipCurrentModal, dispatch]);

  const open = () => {
    if (!!session_shown_control && alreadyShownControl) return;
    setIsOpen(true);
    dispatch(appendToModalPriority(priority));
  };

  const locallyClose = () => {
    setIsOpen(false);
    dispatch(removeFromModalPriority(priority));
    if (session_shown_control) {
      sessionStorage.setItem(_modalShownKey(modalId), '1');
    }
  };

  const close = async () => {
    locallyClose();
  };

  const dismiss = () => {
    locallyClose();
  };

  async function dontShowThisAgain(): Promise<void> {
    const service = new UserService();
    const newSkipModals = [...(modalsToSkip || []), modalId];
    const user = await service.saveUser(
      usr,
      undefined, // userPreference
      undefined, // sendOrdersToEmail
      undefined, // sendFinancialToEmail
      undefined, // userAcceptNotification
      undefined, // searchCustomerType
      undefined, // improvementResearch
      undefined, // privacyTerms
      undefined, // userName
      undefined, // improvementResearchNps
      undefined, // jorneySurveyShowcaseStatus
      undefined, // jorneySurveyHistoryStatus
      undefined, // jorneySurveyItemsStatus
      undefined, // jorneySurveySheetStatus
      undefined, // gerdauProModal,
      newSkipModals
    );
    dispatch(loadUserSuccess(user));
  }

  const isVisible =
    !alreadyShownControl &&
    maxPriority === priority &&
    isOpen &&
    flagEnabled &&
    !skipCurrentModal;

  return [isVisible, { open, close, dismiss, dontShowThisAgain }];
}
